import { AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import { 
  ApiError, 
  UnauthorizedError, 
  ForbiddenError, 
  NotFoundError, 
  ValidationError, 
  InternalServerError 
} from './errors';

/**
 * Interceptor for adding authentication and tenant headers to outgoing requests
 */
export function requestInterceptor(config: InternalAxiosRequestConfig): InternalAxiosRequestConfig {
  const token = localStorage.getItem('cyzor_access_token') || localStorage.getItem('cyzor_auth_token');

  // Let the browser set the multipart boundary for FormData requests.
  if (typeof FormData !== 'undefined' && config.data instanceof FormData && config.headers) {
    delete config.headers['Content-Type'];
    delete config.headers['content-type'];
  }

  if (config.headers) {
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
      localStorage.setItem('cyzor_access_token', token);
      localStorage.setItem('cyzor_auth_token', token);
    } else {
      delete config.headers.Authorization;
    }
  }

  const activeWorkspaceId = localStorage.getItem('cyzor_active_workspace_id');
  if (activeWorkspaceId && config.headers) {
    config.headers['X-Workspace-ID'] = activeWorkspaceId;
  }

  // Normalize API prefix
  if (config.url && !config.url.startsWith('/api') && !config.url.startsWith('http')) {
    config.url = `/api${config.url.startsWith('/') ? '' : '/'}${config.url}`;
  }

  return config;
}

/**
 * Interceptor for mapping standard status codes to typed ApiError exceptions
 */
export function responseErrorInterceptor(error: any): Promise<never> {
  if (!error.response) {
    return Promise.reject(new ApiError(error.message || 'Erro de conexão com o servidor.'));
  }

  const { status, data } = error.response as AxiosResponse;
  const message = data?.message || data?.error || 'Erro ao processar requisição';
  const details = data?.details || null;
  const code = data?.code || null;

  switch (status) {
    case 400:
      return Promise.reject(new ValidationError(message, details));
    case 401:
      localStorage.removeItem('cyzor_access_token');
      localStorage.removeItem('cyzor_auth_token');
      localStorage.removeItem('cyzor_refresh_token');
      localStorage.removeItem('cyzor_refresh_token_legacy');
      localStorage.removeItem('cyzor_user');
      localStorage.removeItem('cyzor_organization');
      localStorage.removeItem('cyzor_roles');
      localStorage.removeItem('cyzor_permissions');
      window.dispatchEvent(new CustomEvent('cyzor_unauthorized'));
      return Promise.reject(new UnauthorizedError(message, details));
    case 403:
      return Promise.reject(new ForbiddenError(message, details));
    case 404:
      return Promise.reject(new NotFoundError(message, details));
    case 500:
    case 502:
    case 503:
    case 504:
      return Promise.reject(new InternalServerError(message, details));
    default:
      return Promise.reject(new ApiError(message, status, code, details));
  }
}
